import { toAppError } from '@repo/api-client';
import { useSession } from '@repo/api-client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { describeAppError } from '@/features/common/describeAppError';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

type Values = z.infer<typeof schema>;

export function ForgotPasswordScreen() {
  const { forgotPassword } = useSession();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await forgotPassword(values.email);
      setSubmitted(true);
    } catch (err) {
      const description = describeAppError(toAppError(err));
      setError(description.message);
    }
  });

  if (submitted) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle={`If an account exists for ${form.getValues('email')}, we've sent password reset instructions there.`}
        footer={
          <Link
            to="/sign-in"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">
          The reset link is valid for one hour. If you don't see it within a few
          minutes, check your spam folder or try again.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to set a new password."
      footer={
        <Link
          to="/sign-in"
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          Back to sign in
        </Link>
      }
    >
      {error ? (
        <Alert variant="danger" className="mb-4">
          <AlertTitle>Couldn't send the email</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field
          label="Email"
          htmlFor="email"
          required
          error={form.formState.errors.email?.message}
        >
          <Input
            type="email"
            autoComplete="email"
            invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
        </Field>
        <Button type="submit" size="lg" loading={form.formState.isSubmitting}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
